import { toast } from "@/components/common/Toast/Toast"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { PERMISSIONS } from "@/constants/permissions"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { TriangleAlert } from "lucide-react"
import { useMemo, useState, type ReactNode } from "react"
import { toastApiError } from "../../../../../api/apiError"
import {
  useLinkUnresolvedSubstances,
  type LinkSubstanceItem,
  type LinkSubstancesOutcome,
} from "../../../../../api/declarationsApi"
import { isValidCasNumber } from "../../../../../utils/casValidation"
import type { StatementCard } from "../useStudioV2State"
import type { SubstanceEntry } from "./substanceTable"

/** One disclosure the register could not connect, by its D6 address. */
export interface UnresolvedDisclosure extends LinkSubstanceItem {
  key: string
  name: string
  cas: string | null
}

const LINKED = new Set(["created", "linked", "already_linked"])

/** The server's per-request cap (`LINK_UNRESOLVED_BATCH_LIMIT`); a larger batch goes
 *  out as several requests of this size. */
export const LINK_BATCH_LIMIT = 200

/** Create-and-link writes master data and the review alike, so it needs the
 *  substance-edit flag and both permissions. */
export function useCanLinkSubstances(): boolean {
  const flagOn = useFeatureFlag("MC_MASTER_DATA_SUBSTANCE_EDIT")
  const { hasPermission } = useUser()
  return (
    flagOn &&
    hasPermission(PERMISSIONS.MC_MASTER_DATA_EDIT) &&
    hasPermission(PERMISSIONS.MC_DECLARATIONS_REVIEW)
  )
}

/** Every unresolved disclosure of the declaration, in card order. */
export function unresolvedDisclosures(
  cards: readonly StatementCard[],
): UnresolvedDisclosure[] {
  return cards.flatMap((card) =>
    card.excluded
      ? []
      : card.conditions.flatMap((c) =>
          c.unresolved && c.disclosureIndex != null
            ? [
                {
                  key: `${card.framework}:${card.groupIndex}:${c.disclosureIndex}`,
                  framework: card.framework,
                  groupIndex: card.groupIndex,
                  disclosureIndex: c.disclosureIndex,
                  name: (c.substance ?? "").trim(),
                  cas: c.cas,
                },
              ]
            : [],
        ),
  )
}

export interface LinkReport {
  level: "success" | "warning"
  key: TranslationKeys
  values?: Record<string, string | number>
}

function sameAddress(a: LinkSubstanceItem, b: LinkSubstanceItem): boolean {
  return (
    a.framework === b.framework &&
    a.groupIndex === b.groupIndex &&
    a.disclosureIndex === b.disclosureIndex
  )
}

function namesOf(
  items: readonly UnresolvedDisclosure[],
  results: readonly LinkSubstanceItem[],
): string[] {
  return items
    .filter((i) => results.some((r) => sameAddress(r, i)))
    .map((i) => i.name || i.cas || "")
}

/** The one toast a create-and-link answers with. A result status this client
 *  does not know counts as not linked. */
export function linkReport(
  items: readonly UnresolvedDisclosure[],
  outcome: LinkSubstancesOutcome,
): LinkReport {
  const done = outcome.results.filter((r) => LINKED.has(r.status))
  if (!outcome.recomputeRouted && done.length > 0) {
    return { level: "warning", key: "MC_DECL_V2_SUBST_LINK_NOT_HEALED" }
  }
  const createOff = outcome.results.filter(
    (r) => r.status === "create_unavailable",
  )
  if (createOff.length > 0) {
    return {
      level: "warning",
      key: "MC_DECL_V2_SUBST_LINK_CREATE_OFF",
      values: { names: namesOf(items, createOff).join(", ") },
    }
  }
  const conflict = outcome.results.filter(
    (r) => r.status === "register_conflict",
  )
  if (conflict.length > 0) {
    return {
      level: "warning",
      key: "MC_DECL_V2_SUBST_LINK_CONFLICT",
      values: { names: namesOf(items, conflict).join(", ") },
    }
  }
  if (done.length === items.length) {
    return done.length === 1
      ? { level: "success", key: "MC_DECL_V2_SUBST_LINK_DONE_ONE" }
      : {
          level: "success",
          key: "MC_DECL_V2_SUBST_LINK_DONE_MANY",
          values: { n: done.length },
        }
  }
  const missed = items
    .filter((i) => !done.some((r) => sameAddress(r, i)))
    .map((i) => i.name || i.cas || "")
  return {
    level: "warning",
    key: "MC_DECL_V2_SUBST_LINK_PARTIAL",
    values: { n: done.length, total: items.length, names: missed.join(", ") },
  }
}

/**
 * Send the items in batches the server accepts. A batch that fails stops the run but
 * keeps what the earlier batches committed: `error` is set and `outcome` holds the
 * results so far, so the report can name what was linked and what was not.
 */
export async function linkInBatches(
  send: (items: LinkSubstanceItem[]) => Promise<LinkSubstancesOutcome>,
  items: readonly UnresolvedDisclosure[],
): Promise<{ outcome: LinkSubstancesOutcome; error: unknown }> {
  const outcome: LinkSubstancesOutcome = { results: [], recomputeRouted: true }
  for (let i = 0; i < items.length; i += LINK_BATCH_LIMIT) {
    try {
      const part = await send(
        items
          .slice(i, i + LINK_BATCH_LIMIT)
          .map(({ framework, groupIndex, disclosureIndex, name }) => ({
            framework,
            groupIndex,
            disclosureIndex,
            name: name || undefined,
          })),
      )
      outcome.results.push(...part.results)
      outcome.recomputeRouted &&= part.recomputeRouted
    } catch (error) {
      return { outcome, error }
    }
  }
  return { outcome, error: null }
}

/** Run one create-and-link and report it in one toast. */
function useLinkAndReport(declId: string) {
  const { translate } = useTranslation()
  const mutation = useLinkUnresolvedSubstances()
  const run = async (items: UnresolvedDisclosure[]) => {
    const { outcome, error } = await linkInBatches(
      (batch) => mutation.mutateAsync({ declarationId: declId, items: batch }),
      items,
    )
    if (error && outcome.results.length === 0) {
      toastApiError(error, translate, "MC_DECL_V2_SUBST_LINK_FAILED")
      return
    }
    const report = linkReport(items, outcome)
    toast[report.level](translate(report.key, report.values))
  }
  return { run, pending: mutation.isPending }
}

/**
 * The inline answer on a substance card the register could not connect: one
 * click creates the org's substance (or finds the existing one) and links it.
 * Never offered without a CAS that passes its check digit, and never from a
 * name alone; the card then says why.
 */
export function UnresolvedSubstanceNotice({
  declId,
  card,
  entry,
  name,
}: {
  declId: string
  card: StatementCard
  entry: SubstanceEntry
  name: string
}) {
  const { translate } = useTranslation()
  const canLink = useCanLinkSubstances()
  const { run, pending } = useLinkAndReport(declId)

  let body: ReactNode
  if (!entry.cas) {
    body = translate("MC_DECL_V2_SUBST_UNRESOLVED_NO_CAS")
  } else if (!isValidCasNumber(entry.cas)) {
    body = translate("MC_DECL_V2_SUBST_UNRESOLVED_BAD_CAS", { cas: entry.cas })
  } else if (!canLink) {
    body = translate("MC_DECL_V2_SUBST_UNRESOLVED_NO_RIGHTS")
  } else {
    const cas = entry.cas
    body = (
      <>
        <span>
          {translate("MC_DECL_V2_SUBST_UNRESOLVED_PROMPT", { name, cas })}
        </span>
        <button
          type="button"
          className="sv2-fwall"
          disabled={pending}
          data-testid="substance-create-and-link"
          onClick={() =>
            run(
              entry.unresolved.map((disclosureIndex) => ({
                key: `${card.framework}:${card.groupIndex}:${disclosureIndex}`,
                framework: card.framework,
                groupIndex: card.groupIndex,
                disclosureIndex,
                name,
                cas,
              })),
            )
          }
        >
          {translate("MC_DECL_V2_SUBST_UNRESOLVED_ACTION")}
        </button>
      </>
    )
  }
  return (
    <div
      className="sv2-cfopen sv2-evgap"
      role="status"
      data-testid="substance-unresolved"
    >
      <TriangleAlert className="sv2-ic" />
      {body}
    </div>
  )
}

/**
 * The declaration-wide batch: every unresolved disclosure with a valid CAS,
 * ticked by default, names prefilled from the document. The ones without a
 * valid CAS are named but never offered.
 */
export function LinkAllUnresolvedSubstances({
  declId,
  cards,
  loadAllCards,
}: {
  declId: string
  cards: readonly StatementCard[]
  /** STEP C3-b3: set in the paged review, where `cards` holds the open regulation
   *  plus whichever others were loaded. "Link all" writes for EVERY regulation, so
   *  the click first loads every regulation's cards and offers exactly what flag off
   *  offers from them — never the cards on screen alone. The button shows, as flag
   *  off's does, only when the cards on screen have something to link; it carries no
   *  count (the full count needs that load) and says so if the load finds nothing. */
  loadAllCards?: () => Promise<StatementCard[]>
}) {
  const { translate } = useTranslation()
  const canLink = useCanLinkSubstances()
  const { run, pending } = useLinkAndReport(declId)
  // Paged: every regulation's cards, as of the click.
  const [loadedCards, setLoadedCards] = useState<StatementCard[] | null>(null)
  const [loading, setLoading] = useState(false)
  const paged = loadAllCards !== undefined
  const all = useMemo(
    () => unresolvedDisclosures(paged ? (loadedCards ?? []) : cards),
    [paged, loadedCards, cards],
  )
  const offered = all.filter((d) => isValidCasNumber(d.cas))
  const skipped = all.filter((d) => !isValidCasNumber(d.cas))
  // Paged: what the cards on screen already show (decides whether the button shows).
  const onScreen = useMemo(
    () =>
      paged
        ? unresolvedDisclosures(cards).filter((d) => isValidCasNumber(d.cas))
            .length
        : 0,
    [paged, cards],
  )
  const [open, setOpen] = useState(false)
  const [unticked, setUnticked] = useState<Set<string>>(new Set())
  const [names, setNames] = useState<Record<string, string>>({})

  if (!canLink) return null
  // Nothing to link on screen: no button at all, disabled or not. (Paged: judged
  // on the cards on screen — the click then loads every regulation and looks.)
  // Paged can miss an unresolved substance on a regulation that is not loaded, where
  // flag off would show the button. Today that never happens: the server's
  // `/statements` and regulation slices never carry `substance_connections`, so
  // `unresolved` is always false on both flags (Notion ticket "Link all unresolved
  // substances never appears", 40955b45c4528165b5f3d4df01adc2d8). When that is
  // fixed, the `/open-items` summary must gain a per-regulation unresolved count and
  // this rule must read it.
  if (!loadAllCards && offered.length === 0) return null
  if (loadAllCards && onScreen === 0 && !open && !loading) return null

  const openPaged = async () => {
    setLoading(true)
    let everyCard: StatementCard[]
    try {
      everyCard = await loadAllCards!()
    } catch (error) {
      setLoading(false)
      toastApiError(error, translate, "MC_DECL_V2_GESTURE_LOAD_FAILED")
      return
    }
    setLoading(false)
    const found = unresolvedDisclosures(everyCard).filter((d) =>
      isValidCasNumber(d.cas),
    )
    if (found.length === 0) {
      toast.success(translate("MC_DECL_V2_SUBST_LINK_ALL_NONE"))
      return
    }
    // A fresh list: ticks and names typed for an earlier list never carry over.
    setUnticked(new Set())
    setNames({})
    setLoadedCards(everyCard)
    setOpen(true)
  }
  const chosen = offered
    .filter((d) => !unticked.has(d.key))
    .map((d) => ({ ...d, name: (names[d.key] ?? d.name).trim() }))
  const canSubmit = !pending && chosen.length > 0

  const toggle = (key: string) =>
    setUnticked((prev) => {
      const next = new Set(prev)
      if (!next.delete(key)) next.add(key)
      return next
    })

  return (
    <>
      {loadAllCards ? (
        <button
          type="button"
          className="sv2-fwall"
          data-testid="substance-link-all"
          disabled={loading || pending}
          aria-busy={loading || undefined}
          onClick={() => void openPaged()}
        >
          {translate(
            loading
              ? "MC_DECL_V2_SUBST_LINK_ALL_LOADING"
              : "MC_DECL_V2_SUBST_LINK_ALL_PAGED_BUTTON",
          )}
        </button>
      ) : (
        <button
          type="button"
          className="sv2-fwall"
          data-testid="substance-link-all"
          onClick={() => setOpen(true)}
        >
          {translate("MC_DECL_V2_SUBST_LINK_ALL_BUTTON", {
            n: offered.length,
          })}
        </button>
      )}
      <Dialog open={open} onOpenChange={setOpen} modal={false}>
        <DialogContent
          className="max-w-md overflow-hidden"
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.stopPropagation()}
        >
          <DialogHeader>
            <DialogTitle>
              {translate("MC_DECL_V2_SUBST_LINK_ALL_TITLE")}
            </DialogTitle>
          </DialogHeader>
          <p className="text-[12px] text-grey5">
            {translate("MC_DECL_V2_SUBST_LINK_ALL_DESC")}
          </p>
          <div className="space-y-1 max-h-64 overflow-y-auto rounded-md border border-grey4 p-2">
            {offered.map((d) => (
              <label
                key={d.key}
                className="flex items-center gap-2 rounded px-1 py-0.5 text-[12.5px]"
              >
                <input
                  type="checkbox"
                  checked={!unticked.has(d.key)}
                  onChange={() => toggle(d.key)}
                />
                <input
                  type="text"
                  className="min-w-0 flex-1 rounded border border-grey4 bg-transparent px-1.5 py-0.5"
                  value={names[d.key] ?? d.name}
                  onChange={(e) =>
                    setNames((prev) => ({ ...prev, [d.key]: e.target.value }))
                  }
                />
                <span className="font-mono text-[11px] text-grey5">
                  {d.cas}
                </span>
              </label>
            ))}
          </div>
          {skipped.length > 0 && (
            <p className="text-[12px] text-grey5">
              {translate("MC_DECL_V2_SUBST_LINK_ALL_SKIPPED", {
                names: skipped.map((d) => d.name || d.cas || "").join(", "),
              })}
            </p>
          )}
          <DialogFooter>
            <button
              type="button"
              className="px-3 py-1.5 text-[13px] text-grey5 hover:text-white"
              onClick={() => setOpen(false)}
            >
              {translate("MC_DECL_V2_SUBST_LINK_ALL_CANCEL")}
            </button>
            <button
              type="button"
              className="rounded-md bg-attention px-3 py-1.5 text-[13px] font-semibold text-black disabled:opacity-40"
              data-testid="substance-link-all-submit"
              disabled={!canSubmit}
              onClick={async () => {
                await run(chosen)
                setOpen(false)
              }}
            >
              {translate("MC_DECL_V2_SUBST_LINK_ALL_SUBMIT", {
                n: chosen.length,
              })}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
